// @flow
import React from 'react';
import injectSheet from 'react-jss';
import Slot from 'shared/editor/Slot';
import Map from 'shared/components/Map';
import type { $Slot } from 'shared/templates/types';
import SlideRightFadeIn from 'shared/transitions/SlideRightFadeIn';
import isElementInViewport from 'shared/lib/isElementInViewport';
import withThemeProvider from 'shared/hoc/withThemeProvider';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

type TemplateSettings = {
  page_title: string,
  map: boolean,
  hero_image: string
}

type Props = {
  classes: Object,
  slots: $Slot[],
  templateSettings: TemplateSettings,
  widgetPresets: Array<Object>,
  path: string
}

type State = {
  animationPlayed: boolean,
  hasError: boolean,
  mapError: boolean
}

class HeroImage extends React.Component<Props, State> {
  props: Props = this.props;

  state = {
    animationPlayed: false,
    hasError: false,
    mapError: false
  };

  container = React.createRef<HTMLElement>();

  componentDidMount = () => {
    this.animationShouldPlay();
  }

  animationShouldPlay = () => {
    const { animationPlayed } = this.state;

    if (!animationPlayed && isElementInViewport(this.container.current)) {
      this.setState({
        animationPlayed: true
      });
    }
  }

  static getDerivedStateFromError(error) {
    // Google Maps library was not loaded
    if (/google/.test(error.message)) {
      return { mapError: true };
    }

    // Unknown error
    console.error(error);

    return { hasError: true };
  }

  render() {
    const {
      classes: c, slots, templateSettings, widgetPresets, path
    } = this.props;
    const { animationPlayed, hasError, mapError } = this.state;

    if (hasError) {
      return null;
    }

    const map = templateSettings.map && (
      <Map
        center={{ lat: 33.9234, lng: -118.2824 }}
        zoom={10}
        markerPosition={{ lat: 33.8634, lng: -118.2824 }}
      />
    );

    const useMapFallback = !templateSettings.map || mapError;

    return (
      <div
        className={c.container}
        ref={this.container}
      >
        <div className={c.menu}>
          <Slot id="menu-bar" slots={slots} widgetPresets={widgetPresets} path={path} />
        </div>
        <div
          className={c.heroContainer}
          style={{ backgroundImage: useMapFallback ? `url(${templateSettings.hero_image})` : 'none' }}
        >
          {!mapError && map}
        </div>
        <div className={c.content}>
          <div className={c.border} />
          <div className={c.centeredContent}>
            <SlideRightFadeIn show={animationPlayed}>
              <div className={c.pageTitle}>
                {templateSettings.page_title}
              </div>
            </SlideRightFadeIn>
            <Slot id="content" slots={slots} widgetPresets={widgetPresets} path={path} />
          </div>
        </div>
        <footer id="footer" className={c.footer}>
          <Slot id="footer" slots={slots} widgetPresets={widgetPresets} path={path} />
        </footer>
      </div>
    );
  }
}

const styles = {
  menu: {
    width: '100%',
    position: 'relative',
    zIndex: '10'
  },
  heroContainer: {
    width: '100%',
    height: '55vh',
    minHeight: '200px',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover'
  },
  border: {
    width: '70%',
    backgroundColor: 'black',
    height: '4px',
    margin: 'auto',
    '@media(max-width: 960px)': {
      width: '100%'
    }
  },
  content: {
    width: '100%',
    background: 'white',
    position: 'relative'
  },
  pageTitle: {
    fontSize: '40px',
    fontWeight: '300',
    letterSpacing: '1px',
    lineHeight: '1.2em',
    fontFamily: FONT_FAMILY_ROBOTO,
    marginBottom: '72px',
    '@media(max-width: 960px)': {
      fontSize: '35px'
    }
  },
  centeredContent: {
    width: '100%',
    maxWidth: '1596px',
    padding: '72px 16.7% 180px',
    margin: 'auto',
    '@media(max-width: 960px)': {
      padding: '72px 36px'
    }
  },
  footer: {
    width: '100%'
  }
};

export default withThemeProvider(injectSheet(styles)(HeroImage));
